<template>
	<div :tabindex="enabled ? 0 : -1" :class="['button-view', emphasis]" type="text" @click="$emit('click', $event)" @mouseenter="hover_on" @mouseleave="hover_off" @focus="focus_on" @blur="focus_off" :style="{
		...surface_style,
		...theme.typography.button,
		..._style
	}">
		<svg-icon v-if="icon" :icon="icon" class="icon" :style="_iconStyle" />
		<div v-if="$slots.default" class="label"><slot /></div>
	</div>
</template>

<script>
	import surface from '@/mixins/surface';
	import hover from '@/mixins/hover';
	import enabled from '@/mixins/enabled';
	import focus from '@/mixins/focus';

	export default {
		mixins: [surface, hover, enabled, focus],
		props: {
			emphasis: {
				type: 		String,
				default: 	'contained',
				validator(v) {
					return ['floating', 'contained', 'text', 'outlined', 'icon'].indexOf(v) !== -1;
				}
			},
			icon: {
				type: String,
				default: null
			},
			colors: {
				type: Object,
				default() {
					return {};
				}
			}
		},
		computed: {
			_colors() {
				var d = {
					...{
						disabled: 		this.theme.colors.disabledPrimary
					},
					...this.colors
				};				

				var r = {
					disabled: 			d.disabled
				};

				// contained, floating, icon
				if (this.emphasis == 'contained' || this.emphasis == 'floating') {
					r = {
						border: 		this.theme.colors.primary,
						borderHover: 	this.theme.colors.primaryVariant,
						borderDisabled:	d.disabled,
						fill:			this.theme.colors.primary,
						fillHover:		this.theme.colors.primaryVariant,
						fillDisabled:	d.disabled,
						label:			this.theme.colors.onPrimary,
						labelHover:		this.theme.colors.onPrimaryVariant,
						labelDisabled:	this.theme.colors.onPrimaryVariant,
						icon:			this.theme.colors.onPrimary,
						iconHover:		this.theme.colors.onPrimaryVariant,
						iconDisabled:	this.theme.colors.onPrimaryVariant
					};
				}

				// text
				if (this.emphasis == 'icon') {
					r = {
						border: 		'rgba(0, 0, 0, 0)',
						borderHover: 	'rgba(0, 0, 0, 0)',
						borderDisabled:	'rgba(0, 0, 0, 0)',
						fill:			'rgba(0, 0, 0, 0)',
						fillHover:		'rgba(0, 0, 0, 0)',
						fillDisabled:	'rgba(0, 0, 0, 0)',
						label:			this.theme.colors.onSurfaceVariant,
						labelHover:		this.theme.colors.onSurface,
						labelDisabled:	d.disabled,
						icon:			this.theme.colors.onSurfaceVariant,
						iconHover:		this.theme.colors.onSurface,
						iconDisabled:	d.disabled
					};
				}

				// text
				if (this.emphasis == 'text') {
					r = {
						border: 		'rgba(0, 0, 0, 0)',
						borderHover: 	'rgba(0, 0, 0, 0)',
						borderDisabled:	'rgba(0, 0, 0, 0)',
						fill:			'rgba(0, 0, 0, 0)',
						fillHover:		'rgba(0, 0, 0, 0)',
						fillDisabled:	'rgba(0, 0, 0, 0)',
						label:			this.theme.colors.onSurfaceVariant,
						labelHover:		this.theme.colors.onSurface,
						labelDisabled:	d.disabled,
						icon:			this.theme.colors.onSurfaceVariant,
						iconHover:		this.theme.colors.onSurface,
						iconDisabled:	d.disabled
					};
				}

				// outlined
				if (this.emphasis == 'outlined') {
					r = {
						border: 		this.theme.colors.onSurfaceVariant,
						borderHover: 	this.theme.colors.onSurface,
						borderDisabled:	d.disabled,
						fill:			'rgba(0, 0, 0, 0)',
						fillHover:		'rgba(0, 0, 0, 0)',
						fillDisabled:	'rgba(0, 0, 0, 0)',
						label:			this.theme.colors.onSurfaceVariant,
						labelHover:		this.theme.colors.onSurface,
						labelDisabled:	d.disabled,
						icon:			this.theme.colors.onSurfaceVariant,
						iconHover:		this.theme.colors.onSurface,
						iconDisabled:	d.disabled
					};
				}

				return {
					...r,
					...this.colors
				};
			},
			_transition() {
				return this.surface_style.transition + ', background-color 300ms ' + this.theme.transitions.cubic + ', color 300ms ' + this.theme.transitions.cubic + ', border-color 300ms ' + this.theme.transitions.cubic;
			},
			_backgroundColor() {
				if (!this.enabled) {
					return this._colors.fillDisabled;
				} else if (this.hover) {
					return this._colors.fillHover;
				} else if (this.focus) {
					return this._colors.fill;
				} else {
					return this._colors.fill;
				}
			},
			_color() {
				if (!this.enabled) {
					return this._colors.labelDisabled;
				} else if (this.hover) {
					return this._colors.labelHover;
				} else if (this.focus) {
					return this._colors.label;
				} else {
					return this._colors.label;
				}
			},
			_iconFill() {
				if (!this.enabled) {
					return this._colors.iconDisabled;
				} else if (this.hover) {
					return this._colors.iconHover;
				} else if (this.focus) {
					return this._colors.icon;
				} else {
					return this._colors.icon;
				}
			},
			_borderColor() {
				if (!this.enabled) {
					return this._colors.disabled;
				} else if (this.hover) {
					return this._colors.borderHover;
				} else if (this.focus) {
					return this._colors.border;
				} else {
					return this._colors.border;
				}
			},
			_borderStyle() {
				return `1px solid ${this._borderColor}`;
			},
			_cursor() {
				return this.enabled ? 'pointer' : 'inherited';
			},
			_style() {
				return {
					backgroundColor: 	this._backgroundColor,
					color: 				this._color,
					transition:			this._transition,
					cursor:				this._cursor,
					border:				this._borderStyle
				};
			},
			_iconStyle() {
				return {
					fill: 				this._iconFill,
					transition: 		this.surface_style.transition + ', fill 300ms ' + this.theme.transitions.cubic
				};
			}
		}
	}
</script>

<style lang="scss" scoped>
	.button-view {
		display: inline-block;
		border-radius: 5px;
		text-decoration: 0;
		box-sizing: border-box;
		outline: none;
		white-space: nowrap;
		padding: 0 20px 0 10px;
		height: 36px;
		vertical-align: middle;

		& > .label {
			display: inline-block;
			vertical-align: middle;
			line-height: 36px;
			padding: 0 0 0 0;
			margin: 0 0 0 10px;
			text-transform: none;
		}

		& > .icon {
			display: inline-block;
			width: 10px;
			height: 10px;
			color: inherit;
			vertical-align: middle;
			margin-left: 10px;
			line-height: 36px;
		}

		&.floating {
			line-height: 48px;
			padding: 0 16px 0 6px;
			border-radius: 24px;
			min-width: 48px;
			height: 48px;

			& > .icon {
				width: 16px;
				height: 16px;
			}
		}

		&.icon {
			& > .icon {
				width: 18px;
				height: 36px;
			}
		}
	}
</style>